// src/components/dashboard/settings/ModuleManagement.js

import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  CircularProgress,
  Switch,
} from "@mui/material";

// Core Stable MUI Icons
import SettingsSuggestRoundedIcon from "@mui/icons-material/SettingsSuggestRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import LockResetRoundedIcon from "@mui/icons-material/LockResetRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";

const ModuleManagement = () => {
  // Live Dark/Light Mode Synchronizer
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const checkTheme = () => {
      const darkActive =
        document.documentElement.classList.contains("dark-theme") ||
        document.body.classList.contains("dark-theme");
      setIsDark(darkActive);
    };

    checkTheme();
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });

    return () => observer.disconnect();
  }, []);

  // Top Tabs: "Rights" | "Roles" | "Modules"
  const [activeTab, setActiveTab] = useState("Rights");

  // Rights Tab Form State
  const [selectedPractice, setSelectedPractice] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const [saving, setSaving] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  // Practices & Roles Options
  const practicesList = [
    "ABC Practice",
    "Dev Test Clinic",
    "Metropolitan Homecare",
    "TruEvv Healthcare",
  ];

  const rolesList = [
    "super_admin",
    "practice_admin",
    "Care Giver",
    "Billing Specialist",
    "Clinical Staff",
  ];

  // Permissions Matrix Dataset for Rights Tab
  const [rightsMatrix, setRightsMatrix] = useState([
    { module: "Dashboard & Analytics", view: true, create: true, edit: true, delete: false },
    { module: "Visits Management", view: true, create: true, edit: true, delete: true },
    { module: "Patients Directory", view: true, create: true, edit: true, delete: false },
    { module: "ICD Diagnosis Codes", view: true, create: false, edit: false, delete: false },
    { module: "Medication Orders", view: true, create: true, edit: true, delete: false },
    { module: "Provider Credentials", view: true, create: false, edit: false, delete: false },
    { module: "Billing & Invoices", view: true, create: false, edit: false, delete: false },
    { module: "System Settings", view: true, create: false, edit: false, delete: false },
  ]);

  // Roles Tab Dataset
  const [rolesTable, setRolesTable] = useState([
    { id: 1, name: "super_admin", usersCount: 2, level: "Tier 1 - Full Access" },
    { id: 2, name: "practice_admin", usersCount: 5, level: "Tier 2 - Practice Scope" },
    { id: 3, name: "Care Giver", usersCount: 14, level: "Tier 3 - Encounter Execution" },
    { id: 4, name: "Billing Specialist", usersCount: 3, level: "Tier 2 - Financial Scope" },
  ]);

  // Modules Tab Dataset
  const [modulesTable, setModulesTable] = useState([
    { id: 1, name: "Telehealth Video Encounter", code: "MOD-TELE", enabled: true },
    { id: 2, name: "Facial Verification EVV", code: "MOD-FEVV", enabled: true },
    { id: 3, name: "Auto Clearinghouse EDI 837", code: "MOD-EDI", enabled: true },
    { id: 4, name: "OBGYN Clinical Datasets", code: "MOD-OBGYN", enabled: true },
    { id: 5, name: "Automated Patient SMS Reminders", code: "MOD-SMS", enabled: false },
  ]);

  // Unified Clinical Design System Palette
  const tealMain = "#1E5E5F";
  const tealButton = "#245E65";
  const addGreen = "#057A44";
  const labelTeal = "#276A6C";
  const pastelTealBg = "#D9EBEF";

  const cardBg = isDark ? "#0F172A" : "#FFFFFF";
  const cardBorder = isDark ? "rgba(255, 255, 255, 0.08)" : "#245E65";
  const softCardBorder = isDark ? "rgba(255, 255, 255, 0.08)" : "#EAEFF4";
  const primaryTextColor = isDark ? "#F8FAFC" : "#0A2540";
  const secondaryTextColor = isDark ? "#94A3B8" : "#64748B";
  const tableHeaderBg = isDark ? "#162238" : "#F4F9F9";
  const rowHoverBg = isDark ? "rgba(30, 94, 95, 0.08)" : "rgba(36, 94, 101, 0.04)";
  const controlBg = isDark ? "rgba(255, 255, 255, 0.03)" : "#FFFFFF";
  const controlBorder = isDark ? "rgba(255, 255, 255, 0.12)" : "#CBD5E1";
  const dropdownBg = isDark ? "#1E293B" : "#FFFFFF";

  const handleTogglePermission = (index, field) => {
    setRightsMatrix((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: !row[field] } : row))
    );
  };

  const handleSaveRights = () => {
    if (!selectedPractice) {
      setToastMessage("Please select a Practice first.");
      return;
    }
    setSaving(true);
    setTimeout(() => {
      setSaving(false);
      setToastMessage(`Rights for ${selectedRole || "All Roles"} updated successfully!`);
    }, 600);
  };

  const handleModuleToggle = (id) => {
    setModulesTable((prev) =>
      prev.map((m) => (m.id === id ? { ...m, enabled: !m.enabled } : m))
    );
  };

  const menuProps = {
    PaperProps: {
      sx: {
        backgroundColor: dropdownBg,
        border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "#B8D6DD"}`,
        borderRadius: "8px",
        color: primaryTextColor,
        boxShadow: isDark ? "0 12px 32px rgba(0, 0, 0, 0.6)" : "0 8px 24px rgba(15, 23, 42, 0.08)",
        "& .MuiMenuItem-root": {
          fontSize: "0.82rem",
          py: 0.8,
          "&:hover": {
            backgroundColor: isDark ? "rgba(255, 255, 255, 0.06)" : pastelTealBg,
          },
          "&.Mui-selected": {
            backgroundColor: isDark ? "rgba(30, 94, 95, 0.35)" : pastelTealBg,
            color: tealButton,
            fontWeight: 700,
          },
        },
      },
    },
  };

  return (
    <Box sx={{ width: "100%", pb: 6 }}>
      {/* 1. TOP HEADER */}
      <Box
        sx={{
          backgroundColor: cardBg,
          borderRadius: "20px",
          border: `1.5px solid ${cardBorder}`,
          p: { xs: 2.2, md: "20px 26px" },
          display: "flex",
          alignItems: "center",
          gap: 2,
          boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.25)" : "0 2px 10px rgba(10, 37, 64, 0.02)",
          mb: 3,
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "12px",
            backgroundColor: tealMain,
            color: "#FFFFFF",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <SettingsSuggestRoundedIcon sx={{ fontSize: "1.85rem" }} />
        </Box>

        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
            <Typography
              sx={{
                fontSize: { xs: "1.2rem", md: "1.35rem" },
                fontWeight: 800,
                color: primaryTextColor,
                letterSpacing: "-0.3px",
                lineHeight: 1.2,
              }}
            >
              Module Management
            </Typography>
            <Chip
              label="RBAC Security"
              size="small"
              sx={{
                height: "22px",
                fontSize: "0.68rem",
                fontWeight: 700,
                backgroundColor: pastelTealBg,
                color: tealButton,
                borderRadius: "6px",
              }}
            />
          </Box>
          <Typography
            sx={{
              fontSize: "0.82rem",
              color: secondaryTextColor,
              fontWeight: 500,
              mt: 0.3,
            }}
          >
            Manage clinical system rights, practitioner role scopes, and feature module toggles
          </Typography>
        </Box>
      </Box>

      {/* 2. MAIN CONTAINER WITH SEGMENTED NAVIGATION */}
      <Box
        sx={{
          backgroundColor: cardBg,
          borderRadius: "20px",
          border: `1.5px solid ${cardBorder}`,
          p: { xs: 2.5, md: "24px 28px" },
          boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.25)" : "0 2px 10px rgba(10, 37, 64, 0.02)",
        }}
      >
        {/* Navigation Tabs */}
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.8,
            p: 0.5,
            borderRadius: "10px",
            backgroundColor: isDark ? "rgba(255, 255, 255, 0.03)" : "#F1F5F9",
            border: `1px solid ${softCardBorder}`,
            mb: 3.5,
          }}
        >
          {["Rights", "Roles", "Modules"].map((tab) => {
            const isActive = activeTab === tab;
            return (
              <Button
                key={tab}
                onClick={() => setActiveTab(tab)}
                sx={{
                  height: "36px",
                  px: 3,
                  borderRadius: "8px",
                  fontSize: "0.82rem",
                  fontWeight: 700,
                  textTransform: "capitalize",
                  backgroundColor: isActive ? tealButton : "transparent",
                  color: isActive ? "#FFFFFF" : secondaryTextColor,
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: isActive ? tealMain : (isDark ? "rgba(255, 255, 255, 0.05)" : "#E2E8F0"),
                    boxShadow: "none",
                  },
                }}
              >
                {tab}
              </Button>
            );
          })}
        </Box>

        {/* TAB 1: RIGHTS MATRIX */}
        {activeTab === "Rights" && (
          <Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "1fr 1fr auto",
                },
                alignItems: "flex-end",
                gap: 2,
                mb: 3,
              }}
            >
              <Box>
                <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: labelTeal, mb: 0.6 }}>
                  Practice <span style={{ color: "#EF4444" }}>*</span>
                </Typography>
                <Select
                  fullWidth
                  displayEmpty
                  size="small"
                  value={selectedPractice}
                  onChange={(e) => {
                    setSelectedPractice(e.target.value);
                    if (!selectedRole) setSelectedRole("practice_admin");
                  }}
                  IconComponent={KeyboardArrowDownRoundedIcon}
                  MenuProps={menuProps}
                  renderValue={(selected) => {
                    if (!selected) {
                      return (
                        <Typography sx={{ color: secondaryTextColor, fontSize: "0.85rem" }}>
                          Select Practice...
                        </Typography>
                      );
                    }
                    return selected;
                  }}
                  sx={{
                    borderRadius: "6px",
                    backgroundColor: controlBg,
                    fontSize: "0.85rem",
                    color: primaryTextColor,
                    "& fieldset": { borderColor: controlBorder },
                    "&:hover fieldset": { borderColor: tealMain },
                    "&.Mui-focused fieldset": { borderColor: tealMain },
                    "& .MuiSelect-icon": { color: secondaryTextColor },
                  }}
                >
                  {practicesList.map((p) => (
                    <MenuItem key={p} value={p}>
                      {p}
                    </MenuItem>
                  ))}
                </Select>
              </Box>

              <Box>
                <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: labelTeal, mb: 0.6 }}>
                  Role Scope
                </Typography>
                <Select
                  fullWidth
                  displayEmpty
                  size="small"
                  disabled={!selectedPractice}
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  IconComponent={KeyboardArrowDownRoundedIcon}
                  MenuProps={menuProps}
                  renderValue={(selected) => {
                    if (!selectedPractice) {
                      return (
                        <Typography sx={{ color: secondaryTextColor, fontSize: "0.85rem" }}>
                          Select Practice First
                        </Typography>
                      );
                    }
                    if (!selected) {
                      return (
                        <Typography sx={{ color: secondaryTextColor, fontSize: "0.85rem" }}>
                          Select Role...
                        </Typography>
                      );
                    }
                    return selected;
                  }}
                  sx={{
                    borderRadius: "6px",
                    backgroundColor: controlBg,
                    fontSize: "0.85rem",
                    color: primaryTextColor,
                    "& fieldset": { borderColor: controlBorder },
                    "&:hover fieldset": { borderColor: tealMain },
                    "&.Mui-focused fieldset": { borderColor: tealMain },
                    "& .MuiSelect-icon": { color: secondaryTextColor },
                  }}
                >
                  {rolesList.map((r) => (
                    <MenuItem key={r} value={r}>
                      {r}
                    </MenuItem>
                  ))}
                </Select>
              </Box>

              <Button
                variant="contained"
                onClick={handleSaveRights}
                disabled={saving}
                startIcon={
                  saving ? (
                    <CircularProgress size={16} sx={{ color: "#FFFFFF" }} />
                  ) : (
                    <SaveRoundedIcon sx={{ fontSize: "1.15rem !important" }} />
                  )
                }
                sx={{
                  height: "40px",
                  backgroundColor: addGreen,
                  color: "#FFFFFF",
                  fontWeight: 700,
                  fontSize: "0.82rem",
                  borderRadius: "6px",
                  px: 3,
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#046337",
                    boxShadow: "none",
                  },
                }}
              >
                {saving ? "SAVING..." : "SAVE"}
              </Button>
            </Box>

            <Typography
              sx={{
                fontSize: "0.95rem",
                fontWeight: 800,
                color: primaryTextColor,
                mb: 1.5,
                letterSpacing: "-0.2px",
              }}
            >
              Module Access Privileges
            </Typography>

            {!selectedPractice ? (
              <Box
                sx={{
                  p: 4,
                  borderRadius: "14px",
                  border: `1px dashed ${softCardBorder}`,
                  textAlign: "center",
                  backgroundColor: isDark ? "rgba(255, 255, 255, 0.01)" : "#FAFCFF",
                }}
              >
                <ShieldOutlinedIcon sx={{ fontSize: "2.2rem", color: secondaryTextColor, opacity: 0.4, mb: 1 }} />
                <Typography sx={{ fontSize: "0.86rem", color: secondaryTextColor, fontWeight: 500 }}>
                  Please select a Practice above to view and configure Rights.
                </Typography>
              </Box>
            ) : (
              <TableContainer
                sx={{
                  borderRadius: "14px",
                  border: `1px solid ${softCardBorder}`,
                  overflowX: "auto",
                }}
              >
                <Table sx={{ minWidth: 680 }}>
                  <TableHead>
                    <TableRow
                      sx={{
                        "& th": {
                          color: tealButton,
                          fontWeight: 700,
                          fontSize: "0.78rem",
                          textTransform: "uppercase",
                          letterSpacing: "0.3px",
                          backgroundColor: tableHeaderBg,
                          borderBottom: `1px solid ${softCardBorder}`,
                          py: 1.5,
                        },
                      }}
                    >
                      <TableCell>Module Scope</TableCell>
                      <TableCell align="center">View (Read)</TableCell>
                      <TableCell align="center">Create</TableCell>
                      <TableCell align="center">Edit</TableCell>
                      <TableCell align="center">Delete</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {rightsMatrix.map((row, index) => (
                      <TableRow
                        key={row.module}
                        sx={{
                          "&:hover": { backgroundColor: rowHoverBg },
                          "& td": {
                            borderBottom: `1px solid ${
                              isDark ? "rgba(255, 255, 255, 0.04)" : "#F1F5F9"
                            }`,
                            py: 1.2,
                          },
                        }}
                      >
                        <TableCell sx={{ fontWeight: 700, color: primaryTextColor, fontSize: "0.85rem" }}>
                          {row.module}
                        </TableCell>

                        <TableCell align="center">
                          <Checkbox
                            size="small"
                            checked={row.view}
                            onChange={() => handleTogglePermission(index, "view")}
                            sx={{ color: secondaryTextColor, "&.Mui-checked": { color: tealMain } }}
                          />
                        </TableCell>

                        <TableCell align="center">
                          <Checkbox
                            size="small"
                            checked={row.create}
                            onChange={() => handleTogglePermission(index, "create")}
                            sx={{ color: secondaryTextColor, "&.Mui-checked": { color: tealMain } }}
                          />
                        </TableCell>

                        <TableCell align="center">
                          <Checkbox
                            size="small"
                            checked={row.edit}
                            onChange={() => handleTogglePermission(index, "edit")}
                            sx={{ color: secondaryTextColor, "&.Mui-checked": { color: tealMain } }}
                          />
                        </TableCell>

                        <TableCell align="center">
                          <Checkbox
                            size="small"
                            checked={row.delete}
                            onChange={() => handleTogglePermission(index, "delete")}
                            sx={{ color: secondaryTextColor, "&.Mui-checked": { color: "#EF4444" } }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {/* TAB 2: ROLES DIRECTORY */}
        {activeTab === "Roles" && (
          <Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography sx={{ fontSize: "0.95rem", fontWeight: 800, color: primaryTextColor }}>
                System Security Roles
              </Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddRoundedIcon sx={{ fontSize: "1.1rem !important" }} />}
                sx={{
                  height: "36px",
                  backgroundColor: addGreen,
                  color: "#FFFFFF",
                  borderRadius: "6px",
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "0.8rem",
                  px: 2,
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#046337",
                    boxShadow: "none",
                  },
                }}
              >
                Create Role
              </Button>
            </Box>

            <TableContainer
              sx={{
                borderRadius: "14px",
                border: `1px solid ${softCardBorder}`,
              }}
            >
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      "& th": {
                        color: tealButton,
                        fontWeight: 700,
                        fontSize: "0.78rem",
                        textTransform: "uppercase",
                        backgroundColor: tableHeaderBg,
                        borderBottom: `1px solid ${softCardBorder}`,
                        py: 1.5,
                      },
                    }}
                  >
                    <TableCell>Role Name</TableCell>
                    <TableCell>Security Scope</TableCell>
                    <TableCell>Active Users</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rolesTable.map((role) => (
                    <TableRow key={role.id} sx={{ "&:hover": { backgroundColor: rowHoverBg } }}>
                      <TableCell sx={{ fontWeight: 700, color: primaryTextColor, fontSize: "0.85rem" }}>
                        {role.name}
                      </TableCell>
                      <TableCell sx={{ color: secondaryTextColor, fontSize: "0.82rem" }}>
                        {role.level}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={`${role.usersCount} Assigned`}
                          size="small"
                          sx={{
                            height: "22px",
                            backgroundColor: pastelTealBg,
                            color: tealButton,
                            fontWeight: 700,
                            fontSize: "0.7rem",
                            borderRadius: "6px",
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Configure Role Permissions">
                          <IconButton size="small" sx={{ color: secondaryTextColor, "&:hover": { color: tealMain } }}>
                            <LockResetRoundedIcon sx={{ fontSize: "1.1rem" }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* TAB 3: FEATURE MODULES */}
        {activeTab === "Modules" && (
          <Box>
            <Typography sx={{ fontSize: "0.95rem", fontWeight: 800, color: primaryTextColor, mb: 2 }}>
              Feature Modules & System Extensions
            </Typography>

            <TableContainer
              sx={{
                borderRadius: "14px",
                border: `1px solid ${softCardBorder}`,
              }}
            >
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      "& th": {
                        color: tealButton,
                        fontWeight: 700,
                        fontSize: "0.78rem",
                        textTransform: "uppercase",
                        backgroundColor: tableHeaderBg,
                        borderBottom: `1px solid ${softCardBorder}`,
                        py: 1.5,
                      },
                    }}
                  >
                    <TableCell>Module Feature</TableCell>
                    <TableCell>Identifier Code</TableCell>
                    <TableCell align="right">Activation Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {modulesTable.map((mod) => (
                    <TableRow key={mod.id} sx={{ "&:hover": { backgroundColor: rowHoverBg } }}>
                      <TableCell sx={{ fontWeight: 700, color: primaryTextColor, fontSize: "0.85rem" }}>
                        {mod.name}
                      </TableCell>
                      <TableCell sx={{ fontFamily: "monospace", color: tealButton, fontWeight: 700, fontSize: "0.82rem" }}>
                        {mod.code}
                      </TableCell>
                      <TableCell align="right">
                        <FormControlLabel
                          control={
                            <Switch
                              checked={mod.enabled}
                              onChange={() => handleModuleToggle(mod.id)}
                              sx={{
                                "& .MuiSwitch-switchBase.Mui-checked": {
                                  color: addGreen,
                                },
                                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                  backgroundColor: addGreen,
                                },
                              }}
                            />
                          }
                          label={
                            <Typography
                              sx={{
                                fontSize: "0.8rem",
                                fontWeight: 700,
                                color: mod.enabled ? "#057A44" : secondaryTextColor,
                              }}
                            >
                              {mod.enabled ? "Active" : "Disabled"}
                            </Typography>
                          }
                          sx={{ m: 0 }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Box>

      {/* Toast Feedback */}
      <Snackbar
        open={Boolean(toastMessage)}
        autoHideDuration={2500}
        onClose={() => setToastMessage("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setToastMessage("")}
          severity={toastMessage.includes("Please") ? "warning" : "success"}
          sx={{
            borderRadius: "8px",
            fontWeight: 700,
            fontSize: "0.84rem",
            backgroundColor: toastMessage.includes("Please")
              ? isDark ? "#78350F" : "#FEF3C7"
              : isDark ? "#064E3B" : "#DCFCE7",
            color: toastMessage.includes("Please")
              ? isDark ? "#FCD34D" : "#92400E"
              : isDark ? "#6EE7B7" : "#15803D",
          }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ModuleManagement;